/**
 * Utility to format global styles configuration into a human-readable
 * prompt section for AI page generation.
 */
import type {
  StylesConfigDto,
  RgbaDto,
  GradientDto,
  ColorDto,
  ButtonDto,
  HeadingLevelDto,
} from '@builder4/shared/dto/styles';

type ColorValue = RgbaDto | GradientDto | string | null;

const isRgba = (value: ColorValue): value is RgbaDto => {
  return (
    value !== null &&
    typeof value === 'object' &&
    'r' in value &&
    'g' in value &&
    'b' in value &&
    'a' in value
  );
};

const isGradient = (value: ColorValue): value is GradientDto => {
  return value !== null && typeof value === 'object' && 'type' in value && 'stops' in value;
};

const formatColorValue = (value: ColorValue): string => {
  if (value === null) {
    return 'none';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (isRgba(value)) {
    return `rgba(${value.r}, ${value.g}, ${value.b}, ${value.a})`;
  }
  if (isGradient(value)) {
    const stopColors = value.stops.map((s) => s.hexa).join(' → ');
    return `${value.type} gradient (${stopColors})`;
  }
  return 'unknown';
};

const formatColor = (color: ColorDto, semanticName: string): string => {
  const value = formatColorValue(color.value);
  return `- ${semanticName}: ${value}`;
};

const formatHeadingRow = (level: string, heading: HeadingLevelDto): string => {
  return `| ${level} | ${heading.desktop.fontSize}px | ${heading.tablet.fontSize}px | ${heading.mobile.fontSize}px | ${heading.fontWeight} | ${heading.lineHeight} |`;
};

const formatButtonVariant = (
  variantName: string,
  xmlVariant: string,
  button: ButtonDto,
): string => {
  const bgColor = formatColorValue(button.backgroundColor);
  const textColor = formatColorValue(button.fontColor);
  const borderRadius = button.medium.borderRadius[0];
  const sizes = `small (${button.small.fontSize}px), medium (${button.medium.fontSize}px), large (${button.large.fontSize}px)`;

  return `### ${variantName} (variant="${xmlVariant}")
- Background: ${bgColor}
- Text: ${textColor}
- Border radius: ${borderRadius}px
- Sizes: ${sizes}`;
};

/**
 * Formats a StylesConfigDto into a human-readable string for inclusion
 * in the AI page generation prompt.
 */
export const formatGlobalStylesForPrompt = (styles: StylesConfigDto): string => {
  const colorMapping: Record<string, string> = {
    color1: 'primary',
    color2: 'secondary',
    color3: 'tertiary',
    color4: 'text',
    color5: 'accent',
    color6: 'background',
  };

  const colorLines = Object.entries(colorMapping)
    .map(([key, semantic]) => formatColor(styles.colors[key], semantic))
    .join('\n');

  const { heading, body } = styles.typography;

  const headingRows = [
    formatHeadingRow('h1', heading.h1),
    formatHeadingRow('h2', heading.h2),
    formatHeadingRow('h3', heading.h3),
    formatHeadingRow('h4', heading.h4),
    formatHeadingRow('h5', heading.h5),
    formatHeadingRow('h6', heading.h6),
  ].join('\n');

  const buttonVariants = [
    formatButtonVariant('primary', 'primary', styles.buttons.button1),
    formatButtonVariant('secondary', 'secondary', styles.buttons.button2),
    formatButtonVariant('accent', 'accent', styles.buttons.button3),
  ].join('\n\n');

  return `# Current Global Styles

## Color Palette
${colorLines}

## Typography

### Headings (Font: ${heading.fontFamily})
| Level | Desktop | Tablet | Mobile | Weight | Line Height |
|-------|---------|--------|--------|--------|-------------|
${headingRows}

### Body Text (Font: ${body.fontFamily})
- small: ${body.small.fontSize}px, Weight: ${body.small.fontWeight}, Line Height: ${body.small.lineHeight}
- medium: ${body.medium.fontSize}px, Weight: ${body.medium.fontWeight}, Line Height: ${body.medium.lineHeight}
- large: ${body.large.fontSize}px, Weight: ${body.large.fontWeight}, Line Height: ${body.large.lineHeight}

## Button Variants

${buttonVariants}`;
};
